import dayjs from 'dayjs'
import { DocumentContext } from 'next/document'

import { globalBoostrapPropsWrapper, globalBootstrap } from '~/utils/globalBootstrap'
import { dayjsToString } from '~/utils/graphqlDataFormatters'
import { StrapiQuery } from '~/utils/strapi'

import { extractBlogArticles } from '~/components/Blog/BlogSection/BlogSectionUtils'
import BlogMainPage from '~/components/Blog/pages/BlogMainPage'
import { getClubConfig } from '~/config/clubConfig'
import { Query } from '~/generated/graphql'
import Layout from '~/layout'
import { gt } from '~/locale'

export const BLOG_PAGE_SIZE = 2

export const BLOG_CONSTS = {
  technical: {
    ids: ['6', '7'],
    labels: ['techniczne', 'technical'],
  },
  sponsored: {
    ids: ['11'],
    labels: ['artykuł sponsorowany'],
  },
}

type RecentArticlesQueryParams = {
  excludedIds: number[]
  excludedCategories: number[]
  offset: number
  limit: number
}

export const RecentArticlesQuery = ({ excludedIds, excludedCategories, offset, limit }: RecentArticlesQueryParams) => {
  const excludedArticleIds = JSON.stringify([...excludedIds.map(id => id.toString())])
  const excludedCategoryIds = JSON.stringify([...excludedCategories.map(id => id.toString()), ...BLOG_CONSTS.technical.ids])

  return `
  query articles {
    articles(
      filters: {
        id: {
          notIn: ${excludedArticleIds}
        },
        category: {
            id: { notIn: ${excludedCategoryIds} },
        },
        or: [
          { expiryDate: { null: true } },
          { expiryDate: { gte: "${dayjsToString(dayjs.parseZone())}" } }
        ]
      }
      sort: ["publishedAt:desc"]
      pagination: {
        limit: ${limit ?? BLOG_PAGE_SIZE},
        start: ${offset ?? 0},
        }
    ) {
      data {
        id
        attributes {
          title
          slug
          lead
          publishedAt
          category {
            data {
              attributes {
                label
              }
            }
          }
          coverImage {
            data {
              attributes {
                alternativeText
                url
              }
            }
          }
        }
        }
      }
    }
`
}

export const MainAndFeaturedQuery = `
  fragment BlogArticle on ComponentBlogBlogArticleComponent {
    article {
      data {
        id
        attributes {
          title
          lead
          slug
          body
          publishedAt
          facebookUrl
          twitterUrl
          instagramUrl
          category {
            data {
              attributes {
                label
              }
            }
          }
          coverImage {
            data {
              attributes {
                alternativeText
                url
              }
            }
          }
        }
      }
    }
  }

  query BlogFeatured {
    blogFeatured {
      data {
        attributes {
          Featured1 {
            ...BlogArticle
          }
          Main {
             ...BlogArticle
          }
          Featured2 {
              ...BlogArticle
          }
          Featured3 {
              ...BlogArticle
          }
          headline
        }
      }
    }
  }
`

export const getServerSideProps = async (ctx: DocumentContext) => {
  const { apolloClient } = await globalBootstrap({ ctx })

  const clubConfig = getClubConfig()
  if (!clubConfig.blogEnabled) {
    return {
      notFound: true,
    }
  }

  const { data: featuredData } = await apolloClient.query<Query['strapi']>({
    query: StrapiQuery,
    variables: { query: MainAndFeaturedQuery },
  })
  if (!featuredData || !featuredData?.strapi?.data) {
    return null
  }

  const { Main, Featured1, Featured2, Featured3, headline } = featuredData.strapi.data?.blogFeatured?.data?.attributes
  const featuredArticlesIds = [Main, Featured1, Featured2, Featured3].map(item => parseInt(item.article.data.id, 10))

  const initialArticlesData = await apolloClient.query<Query['strapi']>({
    query: StrapiQuery,
    variables: {
      query: RecentArticlesQuery({
        excludedIds: featuredArticlesIds,
        excludedCategories: [],
        offset: 0,
        limit: 3,
      }),
    },
  })

  const initialArticles = extractBlogArticles(initialArticlesData)

  return globalBoostrapPropsWrapper(
    { apolloClient },
    {
      props: {
        heroMainArticle: Main.article.data,
        heroLatestArticles: [Featured1, Featured2, Featured3].map(({ article }) => article.data),
        initialArticles,
        featuredArticlesIds,
        headline,
      },
    }
  )
}

export default BlogMainPage

BlogMainPage.getLayout = function (page) {
  return (
    <Layout
      metadata={{
        title: gt.tp('Blog', 'Weekly Magazine'),
        description: gt.tp(
          'Blog',
          'Discover new flavours, the latest culinary news and a behind-the-scenes look at the restaurant world in Weekly magazine.'
        ),
        headline: page.props.headline,
      }}>
      {page}
    </Layout>
  )
}
